import axios from "axios";
import { APIV2 } from "../../../backend";
import { useAppContext } from "../../../hooks/context/AppContext";
import { useState, useMemo } from "react";
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

export const useProcurementApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh(
    (token: any | null, account: any | null) => {
      setIdToken(token);
    }
  );

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken
          ? `Bearer ${idToken}`
          : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error("Error refreshing token:", refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, [idToken, authData.idToken]);

  // Risk API
  const getRisks = async () => {
    try {
      const response = await apiClient.get("/risks");
      return response.data;
    } catch (err) {
      return handleError(err, "fetching risks");
    }
  };
  const getnewRPMOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_New_RPM");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching new RPM Options");
    }
  };

  const getSingleSourceOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_Single_Source");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching Single Source Options");
    }
  };

  const getReachRiskOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_Reach_Risk");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching Reach Risk Options");
    }
  };

  const getPartnerDrivenInnovationOptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get(
        "/lookup/PR_Partner_Driven_Innovation"
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching Partner Driven Innovation Options");
    }
  };

  const getCMOnboardedptions = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_CM_Onboarded");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching CM Onboarded Options");
    }
  };

  // const getnewRPMOptions = () => [
  //   { key: 1, value: "Yes" },
  //   { key: 2, value: "No" },
  // ];

  //   const getSingleSourceOptions = () => [
  //     { key: 1, value: "Yes" },
  //     { key: 2, value: "No" },
  //   ];

  //   const getReachRiskOptions = () => [
  //     { key: 1, value: "Yes" },
  //     { key: 2, value: "No" },
  //   ];

  //   const getPartnerDrivenInnovationOptions = () => [
  //     { key: 1, value: "Yes" },
  //     { key: 2, value: "No" },
  //   ];

  //   const getCMOnboardedptions = () => [
  //     { key: 1, value: "Yes" },
  //     { key: 2, value: "No" },
  //   ];
  const createProcurement = async (payload: any) => {
    try {
      const response = await apiClient.post("/procurement", payload);
      return response.data;
    } catch (err) {
      return handleError(err, "creating procurement");
    }
  };
  const getProcurementDetailsById = async (id: any) => {
    try {
      const response = await apiClient.get(`/procurement/${id}`);
      return response.data;
    } catch (err) {
      return handleError(err, "fetching procurement details");
    }
  };
  const getPRSupplierSegmentation = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_Supplier_Segmentation");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching PR supplier segmentation");
    }
  };

  const getPRSupplier = async (): Promise<any[]> => {
    try {
      const response = await apiClient.get("/lookup/PR_Supplier");
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // @ts-ignore
      return handleError(error, "fetching PR suppliers");
    }
  };
  return {
    getRisks,
    getnewRPMOptions,
    getSingleSourceOptions,
    getReachRiskOptions,
    getPartnerDrivenInnovationOptions,
    getCMOnboardedptions,
    createProcurement,
    getProcurementDetailsById,
    getPRSupplier,
    getPRSupplierSegmentation,
  };
};
